import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useIwEditingStore } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import { useIwRecordingStore } from '@/entities/interwills/stores/iwRecording.store';
import { media } from '@/shared/components';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { RecordingUnfinishedModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

export const RecordingUnfinishedModal: FC<RecordingUnfinishedModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: `recordingUnfinished` });

  const editing = useIwEditingStore();
  const recording = useIwRecordingStore();
  const modals = useModalsStore();

  const hasTestator = Boolean(recording.testator);

  const hasAllWitnesses = useMemo(
    () => Object.values(recording.witnesses).every((item) => item !== null),
    [recording],
  );

  const prefix = useMemo(() => {
    if (!hasAllWitnesses && !hasTestator) {
      return 'testatorAndWitness';
    }

    if (!hasAllWitnesses) {
      return 'witness';
    }

    return 'testator';
  }, [hasAllWitnesses, hasTestator]);

  const onLater = () => {
    modals.closeModal();
    modals.openModal({
      type: 'RecordingConfirmModal',
    });
  };

  const onRecord = () => {
    if (!hasTestator) {
      recording.setRecordingFor({
        name: getBeneficiaryFullName({
          firstName: editing.testator.firstName ?? '',
          lastName: editing.testator.lastName ?? '',
        }),
        role: t('testatorText'),
        witnessId: null,
      });
    } else {
      const firstNonRecorded = Object.keys(recording.witnesses).find(
        (key) => recording.witnesses[key] === null,
      );

      if (!firstNonRecorded) return;
      const witnessData = editing.witnesses.find((item) => item.id === firstNonRecorded);

      if (!witnessData) return;

      recording.setRecordingFor({
        name: getBeneficiaryFullName({
          firstName: witnessData.firstName ?? '',
          lastName: witnessData.lastName ?? '',
        }),
        role: t('witnessText'),
        witnessId: firstNonRecorded,
      });
    }

    modals.closeModal();
    modals.openModal({
      type: 'RecordingModal',
    });
  };

  return (
    <ModalContainerV2 pinBottom title={t(`${prefix}.title`)}>
      <Content>
        <span>{t(`${prefix}.text`)}</span>
        <Buttons>
          <ButtonIw onClick={onLater} type="default">
            {t(`${prefix}.laterBtn`)}
          </ButtonIw>
          <ButtonIw onClick={onRecord}>{t(`${prefix}.nowBtn`)}</ButtonIw>
        </Buttons>
      </Content>
    </ModalContainerV2>
  );
};

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 16px;
  gap: 16px;

  ${media.iwMobile} {
    flex-direction: column-reverse;
  }
`;

const Content = styled.div`
  padding: 24px 0 0;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
